<template lang="pug">
div(class='SliderSwitcher')
	UiCarousel(
		class='SliderSwitcher--Carousel',
		:perView='greaterThan("desktop") ? 1 : greaterThan("tablet") ? 1.3 : 1.2',
		:gap='greaterThan("desktop") ? "xl" : greaterThan("mobile") ? "md" : "sm"',
		ref='slider'
	)
		template(#default='{ carouselItemProps }')
			TransitionGroup(name='slider')
				UiCarouselItem(v-for='service in services', :key='service.id', v-bind='carouselItemProps')
					div(class='SliderSwitcher--Carousel--Item')
						NuxtLink(v-if='greaterThan("mobileWide")', :to='getServicesPageUrl(service.alias)', class='Image')
							UiImage(:image='service.preview', width='400px', height='400px', lazy)
						NuxtLink(:to='getServicesPageUrl(service.alias)', class='Name') {{ service.name }}
						UiProse(
							v-if='service.shortDescription',
							class='Description',
							:html='service.shortDescription',
							awesome
						)
						UiServiceAction(
							class='Action',
							:form='service.form',
							:price='service.price',
							:credit='service.credit',
							:creditPeriod='service.creditPeriod',
							:button='service.button'
						)

	div(v-if='greaterThan("tablet")', class='SliderSwitcher--CarouselNavigation')
		UiButton(
			class='SliderSwitcher--CarouselNavigation--Button',
			:class='{ Disabled: activeSlide === 1 }',
			variant='secondary',
			rounded,
			square,
			paddet,
			size='xl',
			iconLeft='system/arrow-left',
			iconLeftSize='md',
			@click='slidePrev'
		)
		div(class='SliderSwitcher--CarouselNavigation--Count')
			span {{ activeSlide }} / {{ services.length }}
		UiButton(
			class='SliderSwitcher--CarouselNavigation--Button',
			:class='{ Disabled: isEndSlide }',
			variant='secondary',
			rounded,
			square,
			size='xl',
			iconRight='system/arrow-right',
			iconRightSize='md',
			@click='slideNext'
		)
</template>

<script lang="ts" setup>
import { joinURL } from 'ufo';
import { UiCarousel } from '#components';
import { Services } from '~/models/services';
import { Resolver } from '~/models/resolver';

/**
 * Define props
 */
const { services = [] } = defineProps<{
	services: Services.Model[];
}>();

/**
 * Define hooks
 */
const { getPageUrlByType, fetchResolver } = useResolver();
const { greaterThan } = usePlatform();

const slider = ref<InstanceType<typeof UiCarousel> | null>(null);
const activeSlide = computed(() => slider.value?.activeSlide || 1);
const isEndSlide = computed(() => slider.value?.isEndSlide || false);
const slideNext = () => slider.value?.slideNext();
const slidePrev = () => slider.value?.slidePrev();

/**
 * Get services page url by alias
 */
const getServicesPageUrl = (alias: string) =>
	joinURL(getPageUrlByType(Resolver.Page.SERVICES) || '', alias);

/**
 * Define fetch
 */
await fetchResolver();
</script>

<style lang="less" scoped>
.SliderSwitcher {
	margin: 1rem 0 0 0;
	.box(grid; auto; auto; none; 1rem; auto; auto 1fr auto; center; start);

	&--Carousel {
		.grid(1 2, 3 span);
		.box(block; 100%; auto; none);

		&--Item {
			padding: 1.25rem;
			.box(grid; 100%; 100%; @ColorWhite; 1rem; auto 1fr auto; auto; stretch; start);
			.border-radius(@BorderRadiusSmall);
			& > .Name {
				.grid(1 2; 1 span);
				.text(@ColorBase; 1.25em 1.25rem @semibold; left);
			}
			& > .Description {
				.grid(2 3; 1 span);
				.text(@ColorBase; 1.25em 0.625rem @medium; left);
			}
			& > .Action {
				.grid(3 4; 1 span);
			}

			/* >= 500px */
			@media all and (min-width: @mobile) {
				& > .Name {
					font-size: 1.25rem;
				}
				& > .Description {
					font-size: 0.875rem;
				}
			}

			/* >= 800px */
			@media all and (min-width: @tablet) {
				padding: 0 1.875rem 0 0;
				.box(grid; 100%; 100%; @ColorWhite; 0.625rem 1.875rem; 1.25rem auto 1fr auto 1.25rem; auto 1fr; stretch; start);
				.border-radius(@BorderRadiusMedium);

				& > .Image {
					.grid(5 span; 1 2);
					.border-radius(@BorderRadiusMedium);
					.box(block; 15.625rem; 100%; fade(@ColorBase, 15%));
					overflow: hidden;
					& > img {
						object-fit: cover;
						.box(block; 100%; 100%);
					}
				}
				& > .Name {
					.grid(2 3; 2 3);
				}
				& > .Description {
					.grid(3 4; 2 3);
				}
				& > .Action {
					.grid(4 5; 2 3);
				}
			}

			/* >= 1000px */
			@media all and (min-width: @laptop) {
				& > .Image {
					width: 18.75rem;
				}
				& > .Name {
					font-size: 1.75rem;
				}
				& > .Description {
					font-size: 1rem;
					font-weight: @regular;
				}
			}

			/* >= 1200px */
			@media all and (min-width: @desktop) {
				& > .Image {
					.box(block; 18rem; 100%; fade(@ColorBase, 15%));
				}
			}

			/* >= 1300px */
			@media all and (min-width: @large-screen-small) {
				& > .Name {
					font-size: 2.25rem;
					font-weight: @medium;
				}
				& > .Description {
					font-size: 1.125rem;
					font-weight: @regular;
				}
			}
		}
	}

	&--CarouselNavigation {
		.grid(2 3, 3 span);
		.box(grid; auto; auto; none; 2rem; 1fr; 1fr auto 1fr; center; start; column);
		&--Button {
			padding: 1.375rem !important;
			.grid(1 span);

			&.Disabled {
				border-color: transparent;
				background: fade(@ColorBase, 5%);
				.cursor-default();
				.box-shadow(0; 0; 0; 0; transparent) !important;
			}
		}
		&--Count {
			.grid(1 span);
			.text(@ColorBase; 1em 1.5rem @medium);
		}
	}

	/* >= 1000px */
	@media all and (min-width: @laptop) {
		margin: 2rem 0 0 0;
		.box(grid; auto; auto; none; 1rem 2rem; auto; auto 1fr auto; center; start);
	}

	/* >= 1300px */
	@media all and (min-width: @large-screen-small) {
		.box(grid; auto; auto; none; 2rem 3rem; auto; auto 1fr auto; center; start);
	}
}
</style>
